import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiUsers, FiTruck, FiTrendingUp, FiDollarSign, FiCheck, FiX } from 'react-icons/fi';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import theme from '../styles/theme';
import { clientesAPI } from '../services/api';

// Componentes estilizados
const DashboardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
`;

const StatCard = styled.div`
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[4]};
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.$bgColor || theme.colors.primary[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
`;

const StatValue = styled.div`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['3xl']};
  font-weight: 700;
  margin-bottom: ${theme.spacing[1]};
`;

const StatLabel = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
`;

const StatChange = styled.div`
  color: ${props => props.$positive ? theme.colors.success[500] : theme.colors.error[500]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: between;
  margin-bottom: ${theme.spacing[4]};
`;

const CardTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
`;

const CardContent = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.6;
`;

const WelcomeSection = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.primary[700]} 100%);
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  margin-bottom: ${theme.spacing[8]};
  color: ${theme.colors.text.primary};
  text-align: center;
`;

const WelcomeTitle = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin-bottom: ${theme.spacing[2]};
`;

const WelcomeSubtitle = styled.p`
  font-size: ${theme.fontSizes.lg};
  opacity: 0.9;
`;

const ChecklistCard = styled(Card)`
  grid-column: 1 / -1;
  border: 2px solid ${props => props.$allComplete ? theme.colors.success[500] : theme.colors.warning[500]};
  background: ${props => props.$allComplete ? theme.colors.success[50] : theme.colors.warning[50]};

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-column: span 1;
  }
`;

const ChecklistTitle = styled(CardTitle)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${props => props.$allComplete ? theme.colors.success[700] : theme.colors.warning[700]};
`;

const ChecklistList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const ChecklistItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${props => props.$completed ? theme.colors.success[300] : theme.colors.border.primary};
`;

const ChecklistIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$completed ? theme.colors.success[500] : theme.colors.error[500]};
  color: ${theme.colors.text.primary};
  flex-shrink: 0;
`;

const ChecklistText = styled.span`
  flex: 1;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  font-weight: ${props => props.$completed ? '500' : '400'};
`;

const ChecklistMessage = styled.div`
  margin-top: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  background: ${props => props.$allComplete ? theme.colors.success[100] : theme.colors.warning[100]};
  border-radius: ${theme.borderRadius.lg};
  border-left: 4px solid ${props => props.$allComplete ? theme.colors.success[500] : theme.colors.warning[500]};
  color: ${props => props.$allComplete ? theme.colors.success[700] : theme.colors.warning[700]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
`;

const ClienteSelector = styled.select`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  margin-bottom: ${theme.spacing[4]};
  cursor: pointer;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const ResumoChecklistCard = styled(Card)`
  grid-column: 1 / -1;
  border: 2px solid ${theme.colors.border.primary};

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-column: span 1;
  }
`;

const ResumoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[4]};
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
`;

const ResumoStats = styled.div`
  display: flex;
  gap: ${theme.spacing[6]};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: ${theme.spacing[4]};
    width: 100%;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const ResumoStatValue = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${props => props.$type === 'completo' ? theme.colors.success[500] : theme.colors.warning[500]};
`;

const ResumoStatLabel = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const PendentesList = styled.div`
  margin-top: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const PendenteItem = styled.div`
  padding: ${theme.spacing[3]};
  background: ${theme.colors.background.primary};
  border: 2px solid ${theme.colors.warning[400]};
  border-radius: ${theme.borderRadius.lg};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${theme.colors.warning[700]};
    border-color: ${theme.colors.warning[500]};
    transform: translateX(4px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const PendenteNome = styled.span`
  font-weight: 600;
  color: ${theme.colors.text.primary};
  word-break: break-word;
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin-bottom: ${theme.spacing[1]};
`;

const PendenteBadge = styled.span`
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  background: ${theme.colors.warning[500]};
  color: ${theme.colors.text.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
`;

const criarChecklistBase = (overrides = {}) => ({
  temUsuario: false,
  temConfiguracoes: false,
  temInformacoesRevenda: false,
  loading: false,
  ...overrides,
});

const obterNomeCliente = (cliente) => {
  if (!cliente) return 'Cliente';

  const candidatos = [
    typeof cliente.nome_fantasia === 'string' ? cliente.nome_fantasia.trim() : '',
    typeof cliente.nome_social === 'string' ? cliente.nome_social.trim() : '',
    typeof cliente.cliente_nome === 'string' ? cliente.cliente_nome.trim() : '',
  ];

  const nomeValido = candidatos.find((valor) => valor);
  if (nomeValido) {
    return nomeValido;
  }

  const identificador =
    cliente.id ??
    cliente.cliente_id ??
    (typeof cliente.uuid === 'string' ? cliente.uuid : '');

  return identificador ? `Cliente ${identificador}` : 'Cliente';
};

const Dashboard = () => {
  const { user } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [checklist, setChecklist] = useState(criarChecklistBase({ loading: true }));
  const [resumoChecklist, setResumoChecklist] = useState({
    totalCompleto: 0,
    totalPendente: 0,
    clientesPendentes: [],
    loading: true
  });

  useEffect(() => {
    const carregarResumoConfiguracoes = async () => {
      try {
        setResumoChecklist(prev => ({ ...prev, loading: true }));

        const response = await clientesAPI.resumoConfiguracoes();
        if (response.data.success) {
          const dados = response.data.data || {};
          const clientesResumo = (dados.clientes || []).map((cliente) => ({
            ...cliente,
            cliente_nome: obterNomeCliente(cliente),
          }));

          setClientes(clientesResumo);

          setClienteSelecionado((prevSelecionado) => {
            if (clientesResumo.length === 1) {
              return clientesResumo[0];
            }

            if (!prevSelecionado) {
              return null;
            }

            return clientesResumo.find((cliente) => cliente.id === prevSelecionado.id) || null;
          });

          clientesResumo.forEach((cliente) => {
            const estadoChecklist = criarChecklistBase({
              temUsuario: !!cliente.temUsuario,
              temConfiguracoes: !!cliente.temConfiguracoes,
              temInformacoesRevenda: !!cliente.temInformacoesRevenda,
            });

            localStorage.setItem(
              `checklist_configuracao_${cliente.id}`,
              JSON.stringify(estadoChecklist)
            );
          });

          const clientesPendentesNormalizados = (dados.clientesPendentes || []).map((cliente) => ({
            ...cliente,
            cliente_nome: obterNomeCliente(cliente),
          }));

          setResumoChecklist({
            totalCompleto: dados.totalCompleto || 0,
            totalPendente: dados.totalPendente || 0,
            clientesPendentes: clientesPendentesNormalizados,
            loading: false
          });

          if (clientesResumo.length === 0) {
            setChecklist(criarChecklistBase());
            setClienteSelecionado(null);
          }
        } else {
          setClientes([]);
          setClienteSelecionado(null);
          setResumoChecklist({
            totalCompleto: 0,
            totalPendente: 0,
            clientesPendentes: [],
            loading: false
          });
          setChecklist(criarChecklistBase());
        }
      } catch (error) {
        console.error('Erro ao carregar resumo das configurações:', error);
        setResumoChecklist(prev => ({ ...prev, loading: false }));
      }
    };

    carregarResumoConfiguracoes();
  }, []);

  useEffect(() => {
    if (resumoChecklist.loading) {
      return;
    }

    if (!clienteSelecionado) {
      setChecklist(criarChecklistBase());
      return;
    }

    const clienteResumo = clientes.find((cliente) => cliente.id === clienteSelecionado.id);

    if (!clienteResumo) {
      setChecklist(criarChecklistBase());
      return;
    }

    const novoEstado = criarChecklistBase({
      temUsuario: !!clienteResumo.temUsuario,
      temConfiguracoes: !!clienteResumo.temConfiguracoes,
      temInformacoesRevenda: !!clienteResumo.temInformacoesRevenda,
    });

    setChecklist(novoEstado);
    localStorage.setItem(
      `checklist_configuracao_${clienteResumo.id}`,
      JSON.stringify(novoEstado)
    );
  }, [clienteSelecionado, clientes, resumoChecklist.loading]);

  // Dados mockados - em produção viriam da API
  const stats = [
    {
      icon: FiTruck,
      value: '156',
      label: 'Veículos em Estoque',
      change: '+12%',
      positive: true,
      bgColor: theme.colors.primary[500],
    },
    {
      icon: FiUsers,
      value: '89',
      label: 'Clientes Ativos',
      change: '+5%',
      positive: true,
      bgColor: theme.colors.success[500],
    },
    {
      icon: FiTrendingUp,
      value: 'R$ 2.4M',
      label: 'Vendas Este Mês',
      change: '+18%',
      positive: true,
      bgColor: theme.colors.warning[500],
    },
    {
      icon: FiDollarSign,
      value: 'R$ 45K',
      label: 'Faturamento Hoje',
      change: '+8%',
      positive: true,
      bgColor: theme.colors.success[600],
    },
  ];

  return (
    <Layout 
      title="Dashboard" 
      breadcrumbs={[
        { label: 'Dashboard' }
      ]}
    >
      <WelcomeSection>
        <WelcomeTitle>
          Bem-vindo de volta, {user?.nome}!
        </WelcomeTitle>
        <WelcomeSubtitle>
          Aqui está um resumo do que está acontecendo no sistema.
        </WelcomeSubtitle>
      </WelcomeSection>

      <DashboardContainer>
        {stats.map((stat, index) => (
          <StatCard key={index}>
            <StatHeader>
              <StatIcon $bgColor={stat.bgColor}>
                <stat.icon />
              </StatIcon>
            </StatHeader>
            <StatValue>{stat.value}</StatValue>
            <StatLabel>{stat.label}</StatLabel>
            <StatChange $positive={stat.positive}>
              {stat.change}
            </StatChange>
          </StatCard>
        ))}
      </DashboardContainer>

      <ContentGrid>
        <ResumoChecklistCard>
          <CardHeader>
            <CardTitle>Resumo das Configurações</CardTitle>
          </CardHeader>
          <CardContent>
            {resumoChecklist.loading ? (
              <p>Verificando checklists dos clientes...</p>
            ) : (
              <>
                <ResumoHeader>
                  <ResumoStats>
                    <StatItem>
                      <ResumoStatValue $type="completo">{resumoChecklist.totalCompleto}</ResumoStatValue>
                      <ResumoStatLabel>Clientes com Checklist Completa</ResumoStatLabel>
                    </StatItem>
                    <StatItem>
                      <ResumoStatValue $type="pendente">{resumoChecklist.totalPendente}</ResumoStatValue>
                      <ResumoStatLabel>Clientes com Pendências</ResumoStatLabel>
                    </StatItem>
                  </ResumoStats>
                </ResumoHeader>

                {resumoChecklist.clientesPendentes.length > 0 && (
                  <PendentesList>
                    <div style={{ marginBottom: theme.spacing[2], color: theme.colors.text.secondary, fontSize: theme.fontSizes.sm, fontWeight: 600 }}>
                      Clientes com Pendências (clique para verificar):
                    </div>
                    {resumoChecklist.clientesPendentes.map((cliente) => {
                      // Garantir que temos um nome válido
                      const nomeCliente = (cliente.nome_fantasia && cliente.nome_fantasia.trim()) || 
                                        (cliente.nome_social && cliente.nome_social.trim()) || 
                                        (cliente.cliente_nome && cliente.cliente_nome.trim()) || 
                                        `Cliente ${cliente.id}`;
                      
                      // Debug: verificar se o nome está sendo encontrado
                      if (!nomeCliente || nomeCliente === `Cliente ${cliente.id}`) {
                        console.log('Cliente sem nome encontrado:', cliente);
                      }
                      
                      return (
                        <PendenteItem 
                          key={cliente.id}
                          onClick={() => {
                            const clienteResumo = clientes.find(c => c.id === cliente.id) || cliente;
                            setClienteSelecionado(clienteResumo);
                            // Scroll suave até o card de checklist
                            setTimeout(() => {
                              const checklistCard = document.querySelector('[data-checklist-card]');
                              if (checklistCard) {
                                checklistCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                              }
                            }, 100);
                          }}
                          title={`Clique para verificar a checklist de ${nomeCliente}`}
                        >
                          <PendenteNome>
                            {nomeCliente}
                          </PendenteNome>
                          {cliente.pendencias && cliente.pendencias.length > 0 && (
                            <div style={{ display: 'flex', gap: theme.spacing[1], flexWrap: 'wrap' }}>
                              {cliente.pendencias.map((pendencia, index) => (
                                <PendenteBadge key={index}>{pendencia}</PendenteBadge>
                              ))}
                            </div>
                          )}
                        </PendenteItem>
                      );
                    })}
                  </PendentesList>
                )}

                {resumoChecklist.clientesPendentes.length === 0 && resumoChecklist.totalCompleto > 0 && (
                  <div style={{ 
                    padding: theme.spacing[4], 
                    background: theme.colors.success[50], 
                    borderRadius: theme.borderRadius.lg,
                    color: theme.colors.success[700],
                    textAlign: 'center',
                    fontWeight: 500
                  }}>
                    ✅ Todos os clientes têm a checklist completa!
                  </div>
                )}
              </>
            )}
          </CardContent>
        </ResumoChecklistCard>

        <ChecklistCard 
          $allComplete={checklist.temUsuario && checklist.temConfiguracoes && checklist.temInformacoesRevenda}
          data-checklist-card
        >
          <CardHeader>
            <ChecklistTitle $allComplete={checklist.temUsuario && checklist.temConfiguracoes && checklist.temInformacoesRevenda}>
              {checklist.temUsuario && checklist.temConfiguracoes && checklist.temInformacoesRevenda ? (
                <>
                  <FiCheck /> Checklist Completa
                </>
              ) : (
                <>
                  <FiX /> Configuração Necessária
                </>
              )}
            </ChecklistTitle>
          </CardHeader>
          <CardContent>
            {clientes.length > 1 && (
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: theme.spacing[2],
                    color: theme.colors.gray[900],
                    fontSize: theme.fontSizes.sm,
                    fontWeight: 600
                  }}
                >
                  Selecione o cliente:
                </label>
                <ClienteSelector
                  value={clienteSelecionado?.id || ''}
                  onChange={(e) => {
                    const cliente = clientes.find(c => c.id === parseInt(e.target.value));
                    setClienteSelecionado(cliente || null);
                  }}
                >
                  <option value="">Selecione um cliente</option>
                  {clientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nome_fantasia || cliente.nome_social || `Cliente ${cliente.id}`}
                    </option>
                  ))}
                </ClienteSelector>
              </div>
            )}
            {clienteSelecionado && (
              <div
                style={{
                  marginBottom: theme.spacing[4],
                  color: theme.colors.gray[900],
                  fontSize: theme.fontSizes.sm,
                  fontWeight: 600
                }}
              >
                Cliente: <strong>{clienteSelecionado.nome_fantasia || clienteSelecionado.nome_social || `Cliente ${clienteSelecionado.id}`}</strong>
              </div>
            )}
            {checklist.loading ? (
              <p>Verificando configurações...</p>
            ) : !clienteSelecionado && clientes.length > 1 ? (
              <p style={{ color: theme.colors.text.muted }}>Selecione um cliente para verificar a checklist</p>
            ) : (
              <>
                <ChecklistList>
                  <ChecklistItem $completed={checklist.temUsuario}>
                    <ChecklistIcon $completed={checklist.temUsuario}>
                      {checklist.temUsuario ? <FiCheck /> : <FiX />}
                    </ChecklistIcon>
                    <ChecklistText $completed={checklist.temUsuario}>
                      Deve ter ao menos um usuário cadastrado
                    </ChecklistText>
                  </ChecklistItem>

                  <ChecklistItem $completed={checklist.temConfiguracoes}>
                    <ChecklistIcon $completed={checklist.temConfiguracoes}>
                      {checklist.temConfiguracoes ? <FiCheck /> : <FiX />}
                    </ChecklistIcon>
                    <ChecklistText $completed={checklist.temConfiguracoes}>
                      Deve ter as configurações cadastradas
                    </ChecklistText>
                  </ChecklistItem>

                  <ChecklistItem $completed={checklist.temInformacoesRevenda}>
                    <ChecklistIcon $completed={checklist.temInformacoesRevenda}>
                      {checklist.temInformacoesRevenda ? <FiCheck /> : <FiX />}
                    </ChecklistIcon>
                    <ChecklistText $completed={checklist.temInformacoesRevenda}>
                      Deve ter as informações da revenda cadastradas
                    </ChecklistText>
                  </ChecklistItem>
                </ChecklistList>

                {/* Mensagem removida conforme solicitado */}
              </>
            )}
          </CardContent>
        </ChecklistCard>

        <Card>
          <CardHeader>
            <CardTitle>Atividade Recente</CardTitle>
          </CardHeader>
          <CardContent>
            <p>• Novo veículo cadastrado: Honda Civic 2023</p>
            <p>• Cliente João Silva atualizou perfil</p>
            <p>• Venda realizada: Toyota Corolla Cross</p>
            <p>• Sistema atualizado com sucesso</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Mensagens do Sistema</CardTitle>
          </CardHeader>
          <CardContent>
            <p>✅ Todos os sistemas operando normalmente</p>
            <p>📊 Relatório mensal disponível</p>
            <p>🔄 Backup automático realizado</p>
            <p>⚠️ Atualização de segurança recomendada</p>
          </CardContent>
        </Card>
      </ContentGrid>
    </Layout>
  );
};

export default Dashboard;
